import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { IPAMStatCard } from './IPAMStatCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Layers, Plus, Eye, Edit, Trash2, Copy, AlertTriangle, Network,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../../hooks/usePermissions';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkDeleteBar from '../../components/BulkDeleteBar';

const initialFormData = {
  l2_domain_id: '',
  vlan_number: '',
  name: '',
  description: ''
};

const VLANsPage = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions('ipam.vlans');
  const [vlans, setVlans] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  
  // Bulk selection
  const {
    selectedIds,
    isAllSelected,
    isSomeSelected,
    isSelected,
    toggleRow,
    toggleAll,
    clearSelection
  } = useBulkSelection(vlans);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'vlan_number', dir: 'asc' });
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    search: '',
    l2_domain_id: '',
    hasSubnets: '' // 'with', 'without', or '' for all
  });
  
  // Stats
  const [stats, setStats] = useState({ total: 0, with_subnets: 0, empty: 0 });
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState({ domains: [] });
  
  // CRUD State
  const [modalOpen, setModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [copyingFrom, setCopyingFrom] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchDomains = async () => {
    try {
      const response = await api.get('/ipam/l2-domains/all');
      setDomains(response.data || []);
      setFilterOptions(prev => ({ ...prev, domains: response.data || [] }));
    } catch (error) {
      console.error('Error loading domains:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/ipam/vlans/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchVlans = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        page: pagination.page, 
        limit: pagination.limit,
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      
      if (columnFilters.search) params.append('search', columnFilters.search);
      if (columnFilters.l2_domain_id && columnFilters.l2_domain_id !== 'all') {
        params.append('l2_domain_id', columnFilters.l2_domain_id);
      }

      const response = await api.get(`/ipam/vlans?${params}`);
      setVlans(response.data.data || []);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar VLANs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDomains(); fetchStats(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchVlans(); }, [pagination.page, sortConfig]);
  
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchVlans();
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  const handleFilterChange = (field, value) => {
    setColumnFilters(prev => ({ ...prev, [field]: value }));
  };

  // CRUD Handlers
  const handleCreate = () => {
    setEditingItem(null);
    setCopyingFrom(null);
    setFormData(initialFormData);
    setModalOpen(true);
  };

  const handleEdit = (vlan) => {
    setEditingItem(vlan);
    setCopyingFrom(null);
    setFormData({
      l2_domain_id: vlan.l2_domain_id?.toString() || '',
      vlan_number: vlan.vlan_number?.toString() || '',
      name: vlan.name || '',
      description: vlan.description || ''
    });
    setModalOpen(true);
  };

  const handleCopy = (vlan) => {
    setCopyingFrom(`VLAN ${vlan.vlan_number} - ${vlan.name}`);
    setEditingItem(null);
    setFormData({
      l2_domain_id: vlan.l2_domain_id?.toString() || '',
      vlan_number: '', // Unique - empty
      name: '', // Unique - empty
      description: vlan.description || ''
    });
    setCopyModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.l2_domain_id || !formData.vlan_number || !formData.name.trim()) {
      toast.error('L2 Domain, Número y Nombre son requeridos');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        l2_domain_id: parseInt(formData.l2_domain_id),
        vlan_number: parseInt(formData.vlan_number)
      };

      if (editingItem) {
        await api.put(`/ipam/vlans/${editingItem.id}`, payload);
        toast.success('VLAN actualizada');
      } else {
        await api.post('/ipam/vlans', payload);
        toast.success('VLAN creada');
      }

      setModalOpen(false);
      setCopyModalOpen(false);
      setEditingItem(null);
      setCopyingFrom(null);
      setFormData(initialFormData);
      fetchVlans();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/ipam/vlans/${deleteConfirm.id}`);
      toast.success('VLAN eliminada');
      setDeleteConfirm(null);
      fetchVlans();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Filter VLANs based on hasSubnets filter
  const filteredVlans = vlans.filter(vlan => {
    if (columnFilters.hasSubnets === 'with') {
      return (vlan.subnet_count || 0) > 0;
    } else if (columnFilters.hasSubnets === 'without') {
      return (vlan.subnet_count || 0) === 0;
    }
    return true;
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="vlans-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Layers className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-2xl font-bold text-foreground">VLANs</h1>
              <p className="text-sm text-muted-foreground">Redes Virtuales de Capa 2</p>
            </div>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} data-testid="create-vlan-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nueva VLAN
            </Button>
          )}
        </div>

        {/* Stats Cards - Clickeable para filtrar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <IPAMStatCard icon={Layers} value={stats.total} label="Total VLANs"
            selected={columnFilters.hasSubnets === ''}
            onClick={() => { setColumnFilters(prev => ({ ...prev, hasSubnets: '' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
          <IPAMStatCard icon={Network} value={stats.with_subnets} label="Con Subnets" iconClassName="text-green-500"
            selected={columnFilters.hasSubnets === 'with'}
            onClick={() => { setColumnFilters(prev => ({ ...prev, hasSubnets: 'with' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
          <IPAMStatCard icon={AlertTriangle} value={stats.empty} label="Sin Subnets" iconClassName="text-yellow-500"
            selected={columnFilters.hasSubnets === 'without'}
            onClick={() => { setColumnFilters(prev => ({ ...prev, hasSubnets: 'without' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full table-subtle">
            <thead>
              <tr className="border-b border-border/50">
                {canDelete && (
                  <th className="w-10 p-3">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Seleccionar todos"
                      className={isSomeSelected ? 'opacity-50' : ''}
                    />
                  </th>
                )}
                <th 
                  className="text-left p-3 font-medium text-xs uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('vlan_number')}
                >
                  <div className="flex items-center gap-1">
                    VLAN #
                    <SortIcon field="vlan_number" />
                  </div>
                </th>
                <th 
                  className="text-left p-3 font-medium text-xs uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    NOMBRE
                    <SortIcon field="name" />
                  </div>
                </th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wider">
                  L2 DOMAIN
                </th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wider">
                  DESCRIPCIÓN
                </th>
                <th className="text-center p-3 font-medium text-xs uppercase tracking-wider">
                  SUBNETS
                </th>
                <th className="w-28 text-right p-3 font-medium text-xs uppercase tracking-wider">
                  ACCIONES
                </th>
              </tr>
              <tr className="border-b border-border/30 bg-muted/30">
                {canDelete && <th className="p-2"></th>}
                <th className="p-2">
                  <Input
                    placeholder="Filtrar..."
                    value={columnFilters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="h-8 text-xs"
                  />
                </th>
                <th className="p-2"></th>
                <th className="p-2">
                  <Select value={columnFilters.l2_domain_id} onValueChange={(v) => handleFilterChange('l2_domain_id', v)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {filterOptions.domains.map((d) => (
                        <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </th>
                <th className="p-2"></th>
                <th className="p-2"></th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {loading ? (
                <tr>
                  <td colSpan={canDelete ? 7 : 6} className="text-center p-8 text-muted-foreground">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      Cargando...
                    </div>
                  </td>
                </tr>
              ) : filteredVlans.length === 0 ? (
                <tr>
                  <td colSpan={canDelete ? 7 : 6} className="text-center p-8">
                    <Layers className="h-12 w-12 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-muted-foreground">
                      {columnFilters.hasSubnets ? 'No hay VLANs con este filtro' : 'No hay VLANs'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredVlans.map((vlan) => (
                  <tr key={vlan.id} className={`hover:bg-muted/30 ${isSelected(vlan.id) ? 'bg-primary/5' : ''}`} data-testid={`vlan-row-${vlan.id}`}>
                    {canDelete && (
                      <td className="p-3 w-10">
                        <Checkbox
                          checked={isSelected(vlan.id)}
                          onCheckedChange={() => toggleRow(vlan.id)}
                          aria-label={`Seleccionar VLAN ${vlan.vlan_number}`}
                        />
                      </td>
                    )}
                    <td className="p-3">
                      <span className="font-mono font-bold text-primary">{vlan.vlan_number}</span>
                    </td>
                    <td className="p-3 font-medium">{vlan.name}</td>
                    <td className="p-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted">
                        {vlan.l2_domain_name}
                      </span>
                    </td>
                    <td className="p-3 text-muted-foreground">{vlan.description || '-'}</td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {vlan.subnet_count || 0}
                      </span>
                    </td>
                    <td className="p-3 w-28">
                      <div className="flex justify-end gap-0.5">
                        <Link to={`/ipam/vlans/${vlan.id}`}>
                          <button className="action-btn" title="Ver detalle">
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        </Link>
                        {canUpdate && (
                          <button onClick={() => handleEdit(vlan)} className="action-btn action-btn-edit" title="Editar">
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canCreate && (
                          <button onClick={() => handleCopy(vlan)} className="action-btn action-btn-copy" title="Copiar">
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => setDeleteConfirm(vlan)} className="action-btn-delete" title="Eliminar">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
              <span className="text-sm text-muted-foreground">
                {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">Página {pagination.page} de {pagination.pages}</span>
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.pages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar VLAN' : 'Nueva VLAN'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>L2 Domain *</Label>
                <Select value={formData.l2_domain_id} onValueChange={(v) => handleInputChange('l2_domain_id', v)}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Seleccionar dominio" />
                  </SelectTrigger>
                  <SelectContent>
                    {domains.map((d) => (
                      <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Número VLAN *</Label>
                <Input
                  type="number"
                  min="1"
                  max="4094"
                  value={formData.vlan_number}
                  onChange={(e) => handleInputChange('vlan_number', e.target.value)}
                  placeholder="1-4094"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Ej: VLAN_SERVERS"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Descripción opcional"
                  className="mt-1.5"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : (editingItem ? 'Guardar' : 'Crear')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Copy Modal */}
        <Dialog open={copyModalOpen} onOpenChange={setCopyModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="h-5 w-5 text-primary" />
                Copiar VLAN
              </DialogTitle>
              {copyingFrom && <p className="text-sm text-muted-foreground">Copiando desde: {copyingFrom}</p>}
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">Campos únicos (obligatorio)</p>
              </div>
              <div>
                <Label>Número VLAN *</Label>
                <Input type="number" min="1" max="4094" value={formData.vlan_number} onChange={(e) => handleInputChange('vlan_number', e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label>Nombre *</Label>
                <Input value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} className="mt-1.5" />
              </div>
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-sm text-green-600 dark:text-green-400 font-medium">Campos copiados</p>
              </div>
              <div>
                <Label>L2 Domain</Label>
                <Select value={formData.l2_domain_id} onValueChange={(v) => handleInputChange('l2_domain_id', v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {domains.map((d) => (<SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Descripción</Label>
                <Input value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} className="mt-1.5" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCopyModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Creando...' : 'Crear Copia'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground py-4">
              ¿Eliminar VLAN <strong>{deleteConfirm?.vlan_number} - {deleteConfirm?.name}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Bar */}
        {canDelete && (
          <BulkDeleteBar
            selectedIds={selectedIds}
            onClear={clearSelection}
            onSuccess={() => { fetchVlans(); fetchStats(); }}
            endpoint="/ipam/vlans"
            entityName="VLAN"
          />
        )}
      </div>
    </Layout>
  );
};

export default VLANsPage;
